import styled from '@emotion/styled';
import { useResources } from 'datx-jsonapi-react';
import { observer } from 'mobx-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { SkeletonText } from '@/components/core/Skeleton/Skeleton';
import { FontFamily } from '@/enums/index';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { queryEventAnnouncements } from '@/queries/announcements';
import { EventAnnouncement } from '@/resources/EventAnnouncement';
import { BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';
import { StringParam } from '@/utils/url-query-string';

const EmptyState = styled.p<IThemeProps>`
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSans]};
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;

  color: ${({ theme }) => theme.legacy.colors.lightText};
`;

const ItemsContainer = styled.div`
  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    margin-top: 24px;
  }
`;

interface IAnnouncementsProps {
  eventName: string;
  children: (item: EventAnnouncement) => ReactNode;
}

export const Announcements: FC<IAnnouncementsProps> = observer(({ eventName, children, ...restProps }) => {
  const { t } = useTranslation();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const [pollingInterval, setPollingInterval] = useState(0);

  const { data: announcements, error, meta } = useResources(queryEventAnnouncements(eventId), {
    refreshInterval: pollingInterval,
  });

  useEffect(() => {
    if (!meta) return;
    const newInterval = ((meta?.['refreshInterval'] as number) || 0) * 1000;

    if (pollingInterval === newInterval) return;
    setPollingInterval(newInterval);
  }, [meta]);

  if (!announcements && !error) {
    return (
      <ItemsContainer {...restProps}>
        {!announcements &&
          !error &&
          Array.from({ length: 4 }, (__, index) => (
            <SkeletonText key={index} noOfLines={5} css={{ width: '100%', marginBottom: 20 }} />
          ))}
      </ItemsContainer>
    );
  }

  if (!announcements?.length || error) {
    return <EmptyState>{t('announcement.emptyState', { eventName })}</EmptyState>;
  }

  return (
    <div>
      <ItemsContainer {...restProps}>{announcements?.map((announcement) => children(announcement))}</ItemsContainer>
    </div>
  );
});
